/**
 * 单一 AccessSession — 解释 Better Auth + /v1/me。
 * 活跃成员身份只在这里按本地 workspaceId 从 memberships 中选出。
 *
 * Callers: AppProviders、AppShell、RouteGuard、WorkspaceMembershipGate
 */

import {
  meDataSchema,
  type PlatformRole,
  type WorkspacePermission,
  type WorkspaceStatus,
} from "@metaadsmanager/tk-master-contracts";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { z } from "zod";

import { useAuth } from "@/components/providers/auth-provider";
import {
  clearCachedNavAccess,
  writeCachedNavAccess,
} from "@/lib/navigation/nav-permissions-cache";
import {
  notifyNavPermissionsCacheChanged,
  useStaleNavAccess,
} from "@/lib/navigation/use-stale-nav-permissions";
import { meQueryOptions } from "@/lib/query/me";
import type { RouteMembership } from "@/lib/auth/route-access";
import { toRouteMembership } from "@/lib/auth/route-access";
import {
  clearStoredActiveWorkspaceId,
  notifyActiveWorkspaceChanged,
  readStoredActiveWorkspaceId,
  selectActiveMembership,
  subscribeActiveWorkspace,
  writeStoredActiveWorkspaceId,
} from "@/lib/auth/workspace-actor";

type MeData = z.infer<typeof meDataSchema>;
type MeMembership = MeData["memberships"][number];

export type AccessSessionValue = {
  error: unknown;
  me: MeData | undefined;
  membership: MeMembership | null | undefined;
  memberships: readonly MeMembership[];
  membershipReady: boolean;
  permissions: readonly WorkspacePermission[];
  phase: "anonymous" | "loading" | "ready" | "error";
  platformRole: PlatformRole | null | undefined;
  refetch: () => void;
  routeMembership: RouteMembership | null | undefined;
  session: ReturnType<typeof useAuth>["session"];
  /** `phase === "loading"` 时非空；壳层 `use()` 它，与首页分包共用一次 fallback。 */
  shellPending: Promise<void> | null;
  selectWorkspace: (workspaceId: string) => void;
  signOut: () => Promise<void>;
  staleIsWorkspaceOwner: boolean | undefined;
  stalePermissions: readonly WorkspacePermission[] | undefined;
  staleWorkspaceStatus: WorkspaceStatus | undefined;
};

const AccessSessionContext = createContext<AccessSessionValue | null>(null);

export function AccessSessionProvider({ children }: { children: ReactNode }) {
  const { session, status, signOut, refreshSession } = useAuth();
  const userId = session?.user.id;
  const preferredWorkspaceId = useSyncExternalStore(
    subscribeActiveWorkspace,
    () => readStoredActiveWorkspaceId(userId),
    () => null,
  );
  const stale = useStaleNavAccess(userId, preferredWorkspaceId);
  const stalePermissions = stale?.permissions;
  const staleWorkspaceStatus = stale?.workspaceStatus;
  const staleIsWorkspaceOwner = stale?.isWorkspaceOwner;
  const meQuery = useQuery({
    ...meQueryOptions(userId),
    enabled: status === "authenticated" && Boolean(session),
  });
  const sessionBlocking =
    status === "loading" ||
    (status === "authenticated" && meQuery.isPending);
  const shellPendingRef = useRef<{
    promise: Promise<void>;
    resolve: () => void;
  } | null>(null);
  if (sessionBlocking && !shellPendingRef.current) {
    let resolve!: () => void;
    const promise = new Promise<void>((done) => {
      resolve = done;
    });
    shellPendingRef.current = { promise, resolve };
  }
  const shellPending = sessionBlocking
    ? shellPendingRef.current!.promise
    : null;
  useEffect(() => {
    if (sessionBlocking) return;
    const pending = shellPendingRef.current;
    if (!pending) return;
    shellPendingRef.current = null;
    pending.resolve();
  }, [sessionBlocking]);
  const me = meQuery.data?.data;
  const selection = me
    ? selectActiveMembership(me.memberships, preferredWorkspaceId)
    : null;
  const liveMembership = selection?.membership ?? undefined;
  const selectWorkspace = useCallback(
    (workspaceId: string) => {
      if (!userId) return;
      writeStoredActiveWorkspaceId(userId, workspaceId);
      notifyActiveWorkspaceChanged();
    },
    [userId],
  );

  const shouldPersistSelection = selection?.shouldPersist ?? false;
  const resolvedWorkspaceId = selection?.resolvedWorkspaceId ?? null;
  useEffect(() => {
    if (!userId || !shouldPersistSelection) return;
    if (resolvedWorkspaceId) {
      writeStoredActiveWorkspaceId(userId, resolvedWorkspaceId);
    } else {
      clearStoredActiveWorkspaceId(userId);
    }
    notifyActiveWorkspaceChanged();
  }, [resolvedWorkspaceId, shouldPersistSelection, userId]);

  useEffect(() => {
    if (!userId || !me) return;
    if (!liveMembership) {
      clearCachedNavAccess();
      notifyNavPermissionsCacheChanged();
      return;
    }
    if (
      !writeCachedNavAccess({
        userId,
        workspaceId: liveMembership.workspace.id,
        permissions: liveMembership.permissions,
        platformRole: me.platformRole ?? null,
        workspaceStatus: liveMembership.workspace.status,
        isWorkspaceOwner: liveMembership.role.kind === "owner",
      })
    ) {
      return;
    }
    notifyNavPermissionsCacheChanged();
  }, [liveMembership, me, userId]);

  const value = useMemo<AccessSessionValue>(() => {
    const handleSignOut = async () => {
      clearCachedNavAccess();
      notifyNavPermissionsCacheChanged();
      await signOut();
    };

    if (status === "anonymous") {
      return {
        error: undefined,
        me: undefined,
        membership: null,
        memberships: [],
        membershipReady: true,
        permissions: [],
        phase: "anonymous",
        platformRole: null,
        refetch: () => {
          void refreshSession();
        },
        routeMembership: null,
        session: null,
        shellPending,
        selectWorkspace,
        signOut: handleSignOut,
        staleIsWorkspaceOwner: undefined,
        stalePermissions: undefined,
        staleWorkspaceStatus: undefined,
      };
    }

    const membershipReady =
      status !== "loading" && Boolean(session) && (meQuery.isSuccess || meQuery.isError);

    if (status === "loading" || (status === "authenticated" && meQuery.isPending)) {
      return {
        error: undefined,
        me: undefined,
        membership: undefined,
        memberships: [],
        membershipReady: false,
        permissions: stalePermissions ?? [],
        phase: "loading",
        platformRole: stale?.platformRole,
        refetch: () => {
          void meQuery.refetch();
        },
        routeMembership: undefined,
        session,
        shellPending,
        selectWorkspace,
        signOut: handleSignOut,
        staleIsWorkspaceOwner,
        stalePermissions,
        staleWorkspaceStatus,
      };
    }

    if (meQuery.isError && !me) {
      return {
        error: meQuery.error,
        me: undefined,
        membership: undefined,
        memberships: [],
        membershipReady: true,
        permissions: stalePermissions ?? [],
        phase: "error",
        platformRole: stale?.platformRole,
        refetch: () => {
          void meQuery.refetch();
        },
        routeMembership: undefined,
        session,
        shellPending,
        selectWorkspace,
        signOut: handleSignOut,
        staleIsWorkspaceOwner,
        stalePermissions,
        staleWorkspaceStatus,
      };
    }

    const membership = liveMembership ?? null;
    return {
      error: undefined,
      me,
      membership,
      memberships: me?.memberships ?? [],
      membershipReady,
      permissions: membership?.permissions ?? [],
      phase: "ready",
      platformRole: me?.platformRole ?? null,
      refetch: () => {
        void meQuery.refetch();
      },
      routeMembership: membership ? toRouteMembership(membership) : null,
      session,
      shellPending,
      selectWorkspace,
      signOut: handleSignOut,
      staleIsWorkspaceOwner,
      stalePermissions,
      staleWorkspaceStatus,
    };
  }, [
    liveMembership,
    me,
    meQuery,
    refreshSession,
    selectWorkspace,
    session,
    shellPending,
    signOut,
    stale,
    staleIsWorkspaceOwner,
    stalePermissions,
    staleWorkspaceStatus,
    status,
  ]);

  return (
    <AccessSessionContext.Provider value={value}>
      {children}
    </AccessSessionContext.Provider>
  );
}

export function useAccessSession() {
  const context = useContext(AccessSessionContext);
  if (!context) {
    throw new Error("useAccessSession must be used within AccessSessionProvider");
  }
  return context;
}
